import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useAuth } from "./AuthProvider";

const EntitlementsContext = createContext(null);

export function EntitlementsProvider({ children }) {
  const {
    entitlements,
    entLoading,
    entError,
    refreshEntitlements,
    hasFeature: authHasFeature,
    getQuota: authGetQuota,
  } = useAuth();

  const snapshot = entitlements || null;
  const loading = Boolean(entLoading);
  const error = entError || null;

  const { featureSet, featureList, quotaMap } = useMemo(() => {
    const s = snapshot;
    const features = Array.isArray(s?.grantedPermissions)
      ? s.grantedPermissions
      : s?.features?.map(f => f.code) || [];

    const upperCodes = features.map(f => String(f || "").toUpperCase());
    const featureSetLocal = new Set(upperCodes);

    const quotas = Array.isArray(s?.quotas) ? s.quotas : [];
    const quotaMapLocal = new Map();

    for (const q of quotas) {
      const key = (
        q.quotaKey ||
        q.code ||
        q.key ||
        q.QuotaKey ||
        ""
      ).toUpperCase();
      if (!key) continue;
      quotaMapLocal.set(key, q);
    }

    return {
      featureSet: featureSetLocal,
      featureList: upperCodes,
      quotaMap: quotaMapLocal,
    };
  }, [snapshot]);

  const hasFeature = useCallback(
    code => {
      if (!code) return false;
      const key = String(code).toUpperCase();
      if (typeof authHasFeature === "function") {
        return !!authHasFeature(key);
      }
      return featureSet.has(key);
    },
    [authHasFeature, featureSet]
  );

  const getFeature = useCallback(
    code => {
      if (!snapshot || !code) return null;

      if (Array.isArray(snapshot.features)) {
        const key = String(code).toUpperCase();
        return (
          snapshot.features.find(
            f => String(f.code || "").toUpperCase() === key
          ) || null
        );
      }

      const key = String(code).toUpperCase();
      const exists = featureSet.has(key);
      if (!exists) return null;
      return { code: key, allowed: true };
    },
    [snapshot, featureSet]
  );

  const getQuota = useCallback(
    quotaKey => {
      if (!quotaKey) return null;
      const key = String(quotaKey).toUpperCase();

      if (typeof authGetQuota === "function") {
        const quota = authGetQuota(key);
        if (quota) return quota;
      }

      return quotaMap.get(key) || null;
    },
    [authGetQuota, quotaMap]
  );

  const canSpend = useCallback(
    (quotaKey, amount = 1) => {
      if (!quotaKey) return true;
      const q = getQuota(quotaKey);
      if (!q) return true;

      const remaining =
        typeof q.remaining === "number"
          ? q.remaining
          : typeof q.Remaining === "number"
            ? q.Remaining
            : null;

      if (remaining == null) return true;
      return remaining >= amount;
    },
    [getQuota]
  );

  const value = useMemo(
    () => ({
      snapshot,
      loading,
      error,
      refresh: () => refreshEntitlements?.(),
      hasFeature,
      getFeature,
      getQuota,
      canSpend,
      grantedPermissions: featureList,
    }),
    [
      snapshot,
      loading,
      error,
      refreshEntitlements,
      hasFeature,
      getFeature,
      getQuota,
      canSpend,
      featureList,
    ]
  );

  return (
    <EntitlementsContext.Provider value={value}>
      {children}
    </EntitlementsContext.Provider>
  );
}

export function useEntitlements() {
  const ctx = useContext(EntitlementsContext);
  if (!ctx) {
    throw new Error(
      "useEntitlements must be used within an EntitlementsProvider"
    );
  }
  return ctx;
}

export function useQuota(quotaKey) {
  const { getQuota } = useEntitlements();
  if (!quotaKey) return null;
  return getQuota(quotaKey);
}

export function useCanSpend(quotaKey, amount = 1) {
  const { canSpend } = useEntitlements();
  return canSpend(quotaKey, amount);
}
